import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext.js';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage.js';

export type UserRole = 'owner' | 'manager' | 'developer' | 'team_member' | 'viewer';

interface RoleGuardProps {
  allowedRoles: UserRole[];
  children: React.ReactNode;
}

export function RoleGuard({ allowedRoles, children }: RoleGuardProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-brand-teal border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    try {
      if (location.pathname && location.pathname !== '/login') {
        sessionStorage.setItem('tsos_pre_login_path', `${location.pathname}${location.search || ''}`);
      }
    } catch {}
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const role = user.role;

  // Normalized matching: developer and team_member have identical capability profile
  const isAllowed =
    allowedRoles.includes(role) ||
    (role === 'developer' && allowedRoles.includes('team_member')) ||
    (role === 'team_member' && allowedRoles.includes('developer'));

  if (!isAllowed) {
    return <UnauthorizedPage requiredRoles={allowedRoles} />;
  }

  return <>{children}</>;
}

export default RoleGuard;
